// topoEditor/entities/docLink.js — "{123}" becomes an INTERNAL document link (opens the gallery with that
// document). Convention = the document id in braces; the value stores nothing but the id (label is derived).
//
// In the standalone playground the click is INERT (styled token only) — the real gallery-open is a consumer
// concern (wired when the live editor lands in a real field), so the mechanism is not invented here.
//
// The "Doc" toolbar button appears ONLY when a NUMBER is selected (canCreate) — the selection IS the id.

import { escapeHtml } from "../render.js";

const DOC_PATTERN = /\{(\d+)\}/g;

function label(id) {
    return "doc:" + (id || "?");
}

// German fallbacks only — the consumer passes translated labels (infoTextEditor.js).
const DEFAULT_LABELS = { title: "Dokument-Link", button: "Doc", idField: "Dokument-ID:", tokenTitle: "Dokument" };

export function docLinkEntity({ labels = {} } = {}) {
    const text = { ...DEFAULT_LABELS, ...labels };
    return {
        kind: "doclink",
        pattern: DOC_PATTERN,
        parse: (m) => ({ id: m[1] }),
        renderHtml: (data, idx) =>
            '<span class="topo2-link--intern topo2-editor-doclink" data-kind="doclink" data-idx="' + idx +
            '" data-id="' + escapeHtml(data.id || "") + '" title="' + escapeHtml(text.tokenTitle + " " + (data.id || "")) + '">' +
            escapeHtml(label(data.id)) + "</span>",
        // ── Nabe edges ──
        serialize: (data) => "{" + (data.id ?? "") + "}",
        // Frontend: inert internal link (no href — the gallery-open is wired by the consumer later). Uses
        // topo2-link--intern (accent text, hover underline), NOT the base topo2-link, so it gets no external icon.
        // Shows only the NUMBER; the "doc:" prefix sits in a hidden span.prefix (kept in the DOM, likely re-enabled later).
        renderFrontendHtml: (data) =>
            '<a class="topo2-link--intern topo2-doclink" data-doc-id="' + escapeHtml(data.id || "") +
            '"><span class="topo2-doclink-prefix">doc:</span>' + escapeHtml(data.id || "") + "</a>",
        // Contenteditable form: an ATOMIC token (contenteditable=false) — the label is derived from the id,
        // so it is not free-text-editable; edit/remove happen via the popover on click.
        renderLiveHtml: (data) =>
            '<a class="topo2-link--intern topo2-editor-doclink topo2-live-doclink" data-kind="doclink" contenteditable="false" data-id="' +
            escapeHtml(data.id || "") + '" title="' + escapeHtml(text.tokenTitle + " " + (data.id || "")) + '">' +
            escapeHtml(label(data.id)) + "</a>",
        matchElement: (el) =>
            (el.classList && el.classList.contains("topo2-live-doclink"))
                ? { id: (el.dataset && el.dataset.id) || "" }
                : null,
        edit: {
            title: text.title,
            label: text.button,                                    // selection-toolbar button (text, per user)
            fields: [{ key: "id", label: text.idField }],
            canCreate: (selectedText) => /^\d+$/.test((selectedText || "").trim()),  // only a numeric selection
            newData: (selectedText) => ({ id: (selectedText || "").trim() }),         // the selection IS the id
            silentCreate: true,                                    // id already known → no popover on create
            normalize: (values) => ({ id: (values.id || "").trim() }),
            format: (data) => "{" + data.id + "}",
            remove: (data) => data.id,   // unwrap: {id} → the bare id number as plain text (like link → its text)
        },
    };
}
